import React from 'react'
import clsx from 'clsx'
import { Portal } from '@mui/material'
import { RiFunctionLine } from 'react-icons/ri'
import {
  ClipboardTick,
  DocumentText,
  Profile2User,
  ProfileCircle
} from 'iconsax-react'
import { useAppDispatch } from '../../../hooks/redux'
import { useHistory } from 'react-router'

import FloatingActionButton from '../../molecules/FloatingActionButton'
import CategoryModal from '../../molecules/ChatModals/CategoryModal'
import TaskModal from '../../molecules/ChatModals/TaskModal'
import AddCustomerModal from '../../molecules/ChatModals/AddCustomerModal'
import AddUserModal from '../../molecules/ChatModals/AddUserModal'

import { Routes } from '../../../Routes'
import { CATEGORY_TAB } from '../../../providers/TaskPageStateProvider'
import { setCreating } from '../../../slices/article'
import useAccess, { PERMS } from '../../../hooks/useAccess'
import useUserPermissions from '../../../hooks/permissions/useUserPermissions'
import { useTaskSWRMutateCache } from '../../../providers/TaskSWRMutateCache'
import useStyles from './styles'

type ActionButtonProps =
  | {
      screen: 'task'
      tab: number
    }
  | {
      screen: 'user'
      cb: () => void
      canShowUser?: boolean
    }
  | {
      screen: 'article'
    }

type Option = {
  screen: ActionButtonProps['screen']
  title: string
  onClick: () => void
  Icon: (props: any) => JSX.Element
  iconClass?: string
}

const ActionButton: React.FC<ActionButtonProps> = ({ screen, ...props }) => {
  const dispatch = useAppDispatch()
  const history = useHistory()

  const hasPermission = useAccess()
  const { checkCanCreateUser } = useUserPermissions()
  const { updateTasks } = useTaskSWRMutateCache()

  const [showUserModal, setShowUserModal] = React.useState(false)
  const [showContactModal, setShowContactModal] = React.useState(false)
  const [showCategoryModal, setShowCategoryModal] = React.useState(false)
  const [showTaskModal, setShowTaskModal] = React.useState(false)

  const canCreateContact = React.useMemo(
    () => checkCanCreateUser(true),
    [checkCanCreateUser]
  )

  const canCreateArticle = React.useMemo(
    () => hasPermission([PERMS.ArticlesFull, PERMS.ArticlesAdmin]),
    [hasPermission]
  )

  const canCreateUser = React.useMemo(() => {
    // @ts-ignore
    if (screen !== 'user' || !props.canShowUser) {
      return false
    }

    return checkCanCreateUser(false)
  }, [screen, props, checkCanCreateUser])

  const canAddCategory = React.useMemo(() => {
    // @ts-ignore
    if (screen !== 'task' || props.tab !== CATEGORY_TAB) {
      return false
    }

    return hasPermission([PERMS.TasksOwn, PERMS.TasksFull])
  }, [screen, props, hasPermission])

  const canCreateTask = React.useMemo(() => {
    return hasPermission([PERMS.TasksOwn, PERMS.TasksFull, PERMS.TasksAdmin])
  }, [hasPermission])

  const classes = useStyles()

  const addUserOption = React.useMemo<Option>(
    () => ({
      screen: 'user',
      title: 'ADD USER',
      onClick: () => setShowUserModal(true),
      Icon: Profile2User,
      iconClass: classes.addPerson
    }),
    [classes.addPerson]
  )

  const newContactOption = React.useMemo<Option>(
    () => ({
      screen: 'user',
      title: 'NEW CONTACT',
      onClick: () => setShowContactModal(true),
      Icon: ProfileCircle,
      iconClass: classes.addPerson
    }),
    [classes.addPerson]
  )

  const newCategoryOption = React.useMemo<Option>(
    () => ({
      screen: 'task',
      title: 'NEW CATEGORY',
      onClick: () => setShowCategoryModal(true),
      Icon: RiFunctionLine,
      iconClass: classes.addCategory
    }),
    [classes.addCategory]
  )

  const addTaskOption = React.useMemo<Option>(
    () => ({
      screen: 'task',
      title: 'ADD TASK',
      onClick: () => setShowTaskModal(true),
      Icon: ClipboardTick,
      iconClass: classes.addTask
    }),
    [classes.addTask]
  )

  const createArticleOption = React.useMemo<Option>(
    () => ({
      screen: 'article',
      title: 'CREATE ARTICLE',
      onClick: () => {
        history.push(Routes.DOCUMENTS)
        setTimeout(() => {
          dispatch(setCreating(true))
        }, 750)
      },
      Icon: DocumentText,
      iconClass: classes.addArticle
    }),
    [dispatch, history, classes.addArticle]
  )

  const options = React.useMemo(() => {
    const opts: Option[] = []

    if (canCreateUser) {
      opts.push(addUserOption)
    }

    if (canCreateContact) {
      opts.push(newContactOption)
    }

    if (canCreateTask) {
      opts.push(addTaskOption)
    }

    if (canAddCategory) {
      opts.push(newCategoryOption)
    }

    if (canCreateArticle) opts.push(createArticleOption)

    // Sort items from this screen to front
    const sorted = opts.sort((a, b) => {
      const aVal = a.screen === screen ? -1 : 1
      const bVal = b.screen === screen ? -1 : 1
      return aVal - bVal
    })

    return sorted
  }, [
    screen,
    canCreateUser,
    addUserOption,
    canCreateContact,
    newContactOption,
    canCreateTask,
    addTaskOption,
    canAddCategory,
    newCategoryOption,
    createArticleOption,
    canCreateArticle
  ])

  if (options.length === 0) {
    return null
  }

  return (
    <Portal>
      <div className={classes.root}>
        <FloatingActionButton anchorProps={{ className: classes.anchor }}>
          {(onBaseClick, buttonClass, baseIconClass) => (
            <>
              {options.map(({ title, onClick, Icon, iconClass }, i) => (
                <div
                  key={`${screen}-${title}-${i}`}
                  onClick={() => {
                    onBaseClick()
                    onClick()
                  }}
                  className={buttonClass}
                >
                  <Icon className={clsx(baseIconClass, iconClass)} />

                  {title}
                </div>
              ))}
            </>
          )}
        </FloatingActionButton>
      </div>

      {showUserModal && (
        <AddUserModal
          open
          onClose={() => setShowUserModal(false)}
          // @ts-ignore
          cb={props.cb}
          hideInternal={false}
        />
      )}

      {showContactModal && (
        <AddCustomerModal
          open
          onClose={() => setShowContactModal(false)}
          // @ts-ignore
          cb={props.cb}
          hideInternal
        />
      )}

      {showCategoryModal && (
        <CategoryModal open onClose={() => setShowCategoryModal(false)} />
      )}

      {showTaskModal && (
        // @ts-ignore
        <TaskModal
          open
          onClose={() => setShowTaskModal(false)}
          onTaskCreated={task => {
            if (screen === 'task') {
              // @ts-ignore
              updateTasks(props.tab, task)
            }
          }}
          canEdit
          canUpdate
        />
      )}
    </Portal>
  )
}

export default ActionButton
